import { useShare } from "@/components/share/useShare";
import { SITE_URL } from "@/lib/config/env-var.config";
import { Box, Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ArtistAlbumCard from "@/components/ui/ArtistAlbumCard";
import { CreatorHeader } from "@/components/creators/CreatorHeader";
import { CreatorPageSkeleton } from "@/components/creators/CreatorPageSkeleton";
import { InlineAboutEditSection } from "@/components/creators/InlineAboutEditSection";
import { InlineLinksEditSection } from "@/components/creators/InlineLinksEditSection";
import { InlineNameEditSection } from "@/components/creators/InlineNameEditSection";
import FilterBar from "@/components/creators/FilterBar";
import { AboutSection } from "@/components/creators/right-panel/AboutSection";
import { LinksSection } from "@/components/creators/right-panel/LinksSection";
import { ShowMoreButton } from "@/components/creators/ShowMoreButton";
import {
  TrackFilters,
  type FilterType,
} from "@/components/creators/TrackFilters";
import { TrackRowWrapper } from "@/components/creators/TrackRowWrapper";
import { TrackSearch } from "@/components/creators/TrackSearch";
import FilterSection from "@/components/Marketplace/FilterSection";
import LeftNav from "@/components/nav/LeftNav";
import ArtistSelector from "@/components/audiolab/ArtistSelector";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import ContainerWrapper from "@/components/ui/ContainerWrapper";

import { AddImageModal } from "@/components/image/AddImageModal";
import { useArtistThemeFromAvatar } from "@/hooks/useArtistThemeFromAvatar";
import { useAvatarUpload } from "@/hooks/useAvatarUpload";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { formatLocationDisplay } from "@/lib/location/location-utils";
import {
  useCreatorsControllerDetailCreator,
  useCreatorsControllerUpdateCreator,
  getCreatorsControllerDetailCreatorQueryKey,
} from "@/lib/lutely-api/gen/creators/creators";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { invalidateCreatorQueries } from "@/lib/lutely-api/api-utils";
import {
  TracksControllerTracksListSortBy,
  type ListTrackDtoType,
  type UpdateCreatorSocialDto,
} from "@/lib/lutely-api/gen/dtos";
import type { UpdateCreatorLocationDto } from "@/lib/lutely-api/gen/dtos";
import type { ListAlbumDto } from "@/lib/lutely-api/gen/dtos/listAlbumDto";
import type { ListTrackDto } from "@/lib/lutely-api/gen/dtos/listTrackDto";
import { useImagesControllerSetProfilePictureFromAsset } from "@/lib/lutely-api/gen/images/images";
import { useAlbumsInfiniteList } from "@/lib/lutely-api/hooks/useAlbumsInfiniteList";
import { useTracksInfiniteList } from "@/lib/lutely-api/hooks/useTracksInfiniteList";
import { cn, ensureHttpsUrl, hexToRgba } from "@/lib/utils";
import { buildAvatarSrc } from "@/utils/avatarUtils";
import { getArtistMaterialTheme } from "@/utils/getArtistMaterialTheme";
import { getCreatorSlugUrl } from "@/utils/slug.utils";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { toast } from "react-toastify";
import { ClickToCopyButton } from "@/components/buttons/ClickToCopyButton";

type CreatorPageProps = {
  creatorIdOrSlug: string;
  hideFilterSection?: boolean;
  showAudiolabNav?: boolean;
  /**
   * Open the profile as the public sees it, even for its owner. Without this,
   * owners are dropped straight into edit mode (see the effect below).
   */
  forcePublicView?: boolean;
};

export function CreatorPage({
  creatorIdOrSlug,
  hideFilterSection,
  showAudiolabNav,
  forcePublicView,
}: CreatorPageProps) {
  const navigate = useNavigate();
  const isDesktopLayout = useMediaQuery("(min-width: 1024px)");
  const [activeFilter, setActiveFilter] = useState<FilterType>("ALBUM");
  const [hasInitializedFilter, setHasInitializedFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const editModeAutoSetRef = useRef(false);
  const [sectionsDirty, setSectionsDirty] = useState({
    name: false,
    about: false,
    links: false,
  });

  function handleDoneEditing() {
    const hasUnsavedChanges = Object.values(sectionsDirty).some(Boolean);
    if (
      hasUnsavedChanges &&
      !window.confirm("You have unsaved changes. Leave without saving?")
    )
      return;
    setIsEditing(false);
    setSectionsDirty({ name: false, about: false, links: false });
  }
  const [nameSaving, setNameSaving] = useState(false);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [linksSaving, setLinksSaving] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarCacheBuster, setAvatarCacheBuster] = useState<number | null>(
    null,
  );
  const [themeRegenKey, setThemeRegenKey] = useState<string>(
    `${creatorIdOrSlug}-${Date.now()}`,
  );
  const [forceThemeRegen, setForceThemeRegen] = useState(false);
  const [loadMoreAttemptedAt100, setLoadMoreAttemptedAt100] = useState(false);

  // Reset forceThemeRegen after one cycle
  useEffect(() => {
    if (forceThemeRegen) {
      const id = setTimeout(() => setForceThemeRegen(false), 0);
      return () => clearTimeout(id);
    }
  }, [forceThemeRegen]);

  const { share, shareModal } = useShare();
  const { creators: userCreators } = useLoggedInUser();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update themeRegenKey when creatorId changes
  useEffect(() => {
    setThemeRegenKey(`${creatorIdOrSlug}-${Date.now()}`);
  }, [creatorIdOrSlug]);

  // Reset filter initialization when creatorId changes
  useEffect(() => {
    setHasInitializedFilter(false);
    setActiveFilter("ALBUM"); // Reset to default, will be set to ALBUM if available or first available filter
    editModeAutoSetRef.current = false;
    setIsEditing(false);
  }, [creatorIdOrSlug]);

  // Reset load-more-attempted when filter changes
  useEffect(() => {
    setLoadMoreAttemptedAt100(false);
  }, [activeFilter]);

  // Fetch creator data
  const {
    data: creatorData,
    isLoading: isCreatorLoading,
    isFetching: isCreatorFetching,
    refetch: refetchCreatorData,
  } = useCreatorsControllerDetailCreator(creatorIdOrSlug, {
    query: {
      enabled: !!creatorIdOrSlug,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: (failureCount, error) => {
        if (
          (error as { response?: { status?: number } })?.response?.status ===
          404
        )
          return false;
        return failureCount < 3;
      },
    },
  });
  // Previewing the public view drops every owner affordance — edit buttons,
  // inline sections, avatar/banner upload — so the page renders exactly as a
  // fan sees it. Ownership itself is still enforced server-side.
  const canEdit =
    !forcePublicView &&
    userCreators.some((c) => c.creatorId === creatorData?.creatorId);

  const handleShare = () => {
    if (!creatorData) return;
    share({
      url: creatorData.slug
        ? `${SITE_URL}/@${creatorData.slug}`
        : window.location.href,
      title: creatorData.stageName || "This artist",
      kind: "artist",
      artworkUrl: creatorData.profilePictureUrl,
    });
  };

  // Profile pages must always open at the top. Router scroll restoration
  // (keyed to .main-scroll-container) can otherwise land mid-page when
  // switching artists or returning to a previously visited profile.
  // Entering edit mode also starts at the top of the form.
  useEffect(() => {
    document
      .querySelector<HTMLElement>(".main-scroll-container")
      ?.scrollTo({ top: 0 });
    window.scrollTo(0, 0);
  }, [creatorIdOrSlug, isEditing]);

  // Default to edit mode on own profile the first time canEdit resolves to true.
  // Using a ref (not state) so this can re-arm when navigating to a different creator
  // without causing an extra render cycle.
  useEffect(() => {
    if (canEdit && !editModeAutoSetRef.current) {
      setIsEditing(true);
      editModeAutoSetRef.current = true;
    }
  }, [canEdit, creatorIdOrSlug]);

  // Get fallback color from localStorage for backwards compatibility, or use default
  const getStoredColor = (key: string, defaultValue: string): string => {
    if (typeof globalThis === "undefined") return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      return stored || defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const fallbackHex = getStoredColor("artistColor", "#1a1a1a");

  // Extract artist theme from avatar image (optimized with caching and downsampling)
  // Pass creatorId as key to force refetch when navigating to different creators
  // Only force regeneration after avatar upload, not on every view
  const { theme: artistTheme, isLoading: artistThemeLoading } =
    useArtistThemeFromAvatar(
      creatorData?.profilePictureUrl,
      fallbackHex,
      themeRegenKey, // Force refetch when themeRegenKey changes (includes creatorId + timestamp)
      forceThemeRegen, // Only force regeneration after upload
    );

  // Use accent color from theme or fallback
  const accent = artistTheme?.accent ?? fallbackHex;

  // Generate Material theme from accent color for full theme tokens
  const fullTheme = useMemo(() => getArtistMaterialTheme(accent), [accent]);

  // Destructure theme tokens for cleaner usage
  const { primary, primaryInk, onPrimary, outline } = fullTheme;

  const queryClient = useQueryClient();

  const { handleAvatarUpload: uploadAvatarFile, isUploading } = useAvatarUpload(
    {
      creatorId: creatorData?.creatorId ?? "",
    },
  );

  const setProfileFromLibrary = useImagesControllerSetProfilePictureFromAsset();

  const handleAvatarUpload = useCallback(() => {
    if (!canEdit) return;
    setShowAvatarModal(true);
  }, [canEdit]);

  function onAvatarSaved() {
    setAvatarCacheBuster(Date.now());
    setForceThemeRegen(true);
    setThemeRegenKey(`${creatorData?.creatorId}-upload-${Date.now()}`);
    window.dispatchEvent(new Event("avatarUpdated"));
  }

  // One API call per track type for filter pill counts
  const songsCountQuery = useTracksInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      type: "SONG",
      pageSize: 100,
      sortBy: TracksControllerTracksListSortBy.uploaded,
      sortDirection: "desc",
    },
    { enabled: !!creatorData?.creatorId },
  );

  const beatsCountQuery = useTracksInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      type: "BEAT",
      pageSize: 100,
      sortBy: TracksControllerTracksListSortBy.uploaded,
      sortDirection: "desc",
    },
    { enabled: !!creatorData?.creatorId },
  );

  const soundkitsCountQuery = useTracksInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      type: "SOUNDKIT",
      pageSize: 100,
      sortBy: TracksControllerTracksListSortBy.uploaded,
      sortDirection: "desc",
    },
    { enabled: !!creatorData?.creatorId },
  );

  const albumsCheckQuery = useAlbumsInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      pageSize: 100,
    },
    {
      enabled: !!creatorData?.creatorId,
    },
  );

  // Wait for count queries to finish before determining available filters
  const isContentCheckLoading =
    songsCountQuery.isLoading ||
    beatsCountQuery.isLoading ||
    soundkitsCountQuery.isLoading ||
    albumsCheckQuery.isLoading;

  // Count from each type-specific query (first page, up to 100 per type)
  const trackTypeCounts = useMemo(() => {
    const songs =
      songsCountQuery.data?.pages.flatMap((p) => p.content || []) ?? [];
    const beats =
      beatsCountQuery.data?.pages.flatMap((p) => p.content || []) ?? [];
    const soundkits =
      soundkitsCountQuery.data?.pages.flatMap((p) => p.content || []) ?? [];
    return {
      SONG: songs.length,
      BEAT: beats.length,
      SOUNDKIT: soundkits.length,
    };
  }, [songsCountQuery.data, beatsCountQuery.data, soundkitsCountQuery.data]);

  // Determine which filters should be shown based on available content
  // Only compute when queries have finished loading to avoid flickering
  const availableFilters = useMemo(() => {
    if (isContentCheckLoading) {
      return [];
    }

    const filters: FilterType[] = [];

    // Songs intentionally hidden.
    const albumsContent = albumsCheckQuery.data?.pages[0]?.content ?? [];
    if (albumsContent.length > 0) {
      filters.push("ALBUM");
    }

    // if (trackTypeCounts.SONG > 0) filters.push("SONG");
    // Beats and Sound Kits hidden along with the Creator Marketplace removal.
    // if (trackTypeCounts.BEAT > 0) filters.push("BEAT");
    // if (trackTypeCounts.SOUNDKIT > 0) filters.push("SOUNDKIT");

    return filters;
  }, [trackTypeCounts, albumsCheckQuery.data, isContentCheckLoading]);

  // Count of items per filter type (for pill labels)
  // Show "100+" when endpoint returned 100 items and nextPage indicates more exist
  const filterCounts = useMemo(() => {
    const counts: Partial<Record<FilterType, number | string>> = {};

    const albumsContent =
      albumsCheckQuery.data?.pages.flatMap((p) => p.content || []) ?? [];
    if (albumsContent.length > 0) {
      const hasMoreAlbums =
        albumsContent.length === 100 && (albumsCheckQuery.hasNextPage ?? false);
      counts.ALBUM = hasMoreAlbums ? "100+" : albumsContent.length;
    }

    if (trackTypeCounts.SONG > 0) {
      const hasMoreSongs =
        trackTypeCounts.SONG === 100 && (songsCountQuery.hasNextPage ?? false);
      counts.SONG = hasMoreSongs ? "100+" : trackTypeCounts.SONG;
    }
    if (trackTypeCounts.BEAT > 0) {
      const hasMoreBeats =
        trackTypeCounts.BEAT === 100 && (beatsCountQuery.hasNextPage ?? false);
      counts.BEAT = hasMoreBeats ? "100+" : trackTypeCounts.BEAT;
    }
    if (trackTypeCounts.SOUNDKIT > 0) {
      const hasMoreSoundkits =
        trackTypeCounts.SOUNDKIT === 100 &&
        (soundkitsCountQuery.hasNextPage ?? false);
      counts.SOUNDKIT = hasMoreSoundkits ? "100+" : trackTypeCounts.SOUNDKIT;
    }

    return counts;
  }, [
    trackTypeCounts,
    albumsCheckQuery.data,
    albumsCheckQuery.hasNextPage,
    songsCountQuery.hasNextPage,
    beatsCountQuery.hasNextPage,
    soundkitsCountQuery.hasNextPage,
  ]);

  // Set default filter: albums first if available, otherwise first available filter
  useEffect(() => {
    if (!hasInitializedFilter && availableFilters.length > 0) {
      if (availableFilters.includes("ALBUM")) {
        setActiveFilter("ALBUM");
      } else {
        // Set to first available filter
        setActiveFilter(availableFilters[0]);
      }
      setHasInitializedFilter(true);
    }
  }, [availableFilters, hasInitializedFilter]);

  // Reset active filter if it's not available (after initialization)
  useEffect(() => {
    if (
      hasInitializedFilter &&
      availableFilters.length > 0 &&
      !availableFilters.includes(activeFilter)
    ) {
      // Set to first available filter if current one is not available
      setActiveFilter(availableFilters[0]);
    }
  }, [availableFilters, activeFilter, hasInitializedFilter, setActiveFilter]);

  // Determine track type filter for API (undefined means "all")
  const trackTypeFilter: ListTrackDtoType | undefined =
    activeFilter === "SONG" ||
    activeFilter === "BEAT" ||
    activeFilter === "SOUNDKIT"
      ? activeFilter
      : undefined;

  // Fetch tracks with server-side filtering and sorting
  const tracksQuery = useTracksInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      type: trackTypeFilter,
      pageSize: 20,
      search: debouncedSearchQuery || undefined,
      sortBy: TracksControllerTracksListSortBy.uploaded,
      sortDirection: "desc",
    },
    {
      enabled: !!creatorData?.creatorId && activeFilter !== "ALBUM",
    },
  );

  // Fetch albums
  const albumsQuery = useAlbumsInfiniteList(
    {
      creatorId: creatorData?.creatorId ?? "",
      published: true,
      pageSize: 20,
    },
    {
      enabled: !!creatorData?.creatorId && activeFilter === "ALBUM",
    },
  );

  // Get tracks from query (already sorted server-side)
  // Deduplicate by trackId to prevent duplicate keys
  const displayedTracks: ListTrackDto[] = useMemo(() => {
    const allTracks =
      tracksQuery.data?.pages.flatMap((page) => page.content || []) || [];
    // Use Map to deduplicate by trackId, keeping the first occurrence
    const uniqueTracks = new Map<string, ListTrackDto>();
    for (const track of allTracks) {
      if (track.trackId && !uniqueTracks.has(track.trackId)) {
        uniqueTracks.set(track.trackId, track);
      }
    }
    return Array.from(uniqueTracks.values());
  }, [tracksQuery.data]);

  // Determine if there are more pages to load
  // Show when display query has more, or when we have 100 items (count query pageSize) and count query has more
  const albumsCount =
    albumsQuery.data?.pages.flatMap((p) => p.content ?? []).length ?? 0;
  const trackCountQueryHasNext =
    activeFilter === "SONG"
      ? songsCountQuery.hasNextPage
      : activeFilter === "BEAT"
        ? beatsCountQuery.hasNextPage
        : activeFilter === "SOUNDKIT"
          ? soundkitsCountQuery.hasNextPage
          : false;
  const hasMoreAlbums =
    (albumsQuery.hasNextPage ?? false) ||
    (albumsCount === 100 &&
      (albumsCheckQuery.hasNextPage ?? false) &&
      !loadMoreAttemptedAt100);
  const hasMoreTracks =
    (tracksQuery.hasNextPage ?? false) ||
    (displayedTracks.length === 100 &&
      trackCountQueryHasNext &&
      !loadMoreAttemptedAt100);
  const hasMorePages = activeFilter === "ALBUM" ? hasMoreAlbums : hasMoreTracks;

  // Reset load-more-attempted when we successfully load more (count > 100)
  useEffect(() => {
    const count =
      activeFilter === "ALBUM" ? albumsCount : displayedTracks.length;
    if (count > 100) {
      setLoadMoreAttemptedAt100(false);
    }
  }, [activeFilter, albumsCount, displayedTracks.length]);

  const isFetchingMore =
    activeFilter === "ALBUM"
      ? (albumsQuery.isFetchingNextPage ?? false)
      : (tracksQuery.isFetchingNextPage ?? false);

  const handleLoadMore = () => {
    const count =
      activeFilter === "ALBUM" ? albumsCount : displayedTracks.length;
    if (count === 100) {
      setLoadMoreAttemptedAt100(true);
    }
    if (activeFilter === "ALBUM") {
      albumsQuery.fetchNextPage();
    } else {
      tracksQuery.fetchNextPage();
    }
  };

  // Loading state (for filter changes) - reusable for both albums and tracks
  const isLoading =
    activeFilter === "ALBUM"
      ? albumsQuery.isLoading && !albumsQuery.isError && !albumsQuery.data
      : tracksQuery.isLoading && !tracksQuery.isError && !tracksQuery.data;

  // Only show full page skeleton on initial load when creator data is not available
  const isInitialLoading =
    isCreatorLoading ||
    artistThemeLoading ||
    (!creatorData && (isCreatorLoading || isCreatorFetching));

  const { mutateAsync: updateCreator } = useCreatorsControllerUpdateCreator();

  const saveProfile = useCallback(
    async (partial: {
      stageName?: string;
      slug?: string | null;
      bio?: string | null;
      location?: UpdateCreatorLocationDto | null;
      socials?: UpdateCreatorSocialDto[];
    }) => {
      if (!creatorData?.creatorId) return;
      const baseLocation = creatorData.location
        ? {
            city: creatorData.location.city ?? null,
            state: creatorData.location.state ?? null,
            country: creatorData.location.country ?? null,
            zip: creatorData.location.zip ?? null,
          }
        : null;
      const baseSocials = (creatorData.socials ?? [])
        .filter((s) => !!s?.channel?.code && !!ensureHttpsUrl(s.url))
        .map((s) => ({
          channelCode: s.channel.code,
          url: ensureHttpsUrl(s.url) ?? "",
        }))
        .filter((s) => !!s.url) as UpdateCreatorSocialDto[];
      await updateCreator({
        data: {
          creatorId: creatorData.creatorId,
          isActive: true,
          stageName: creatorData.stageName || "",
          slug: creatorData.slug ?? null,
          bio: creatorData.bio ?? null,
          location: baseLocation,
          socials: baseSocials,
          ...partial,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getCreatorsControllerDetailCreatorQueryKey(creatorIdOrSlug),
      });
      toast.success("Profile updated!");
    },
    [creatorData, updateCreator, queryClient, creatorIdOrSlug],
  );

  const saveName = useCallback(
    async (name: string, slug: string | null) => {
      setNameSaving(true);
      try {
        const prevSlug = creatorData?.slug;
        await saveProfile({ stageName: name, slug });
        if (slug && slug !== prevSlug) {
          navigate({ to: `/@${slug}` });
        }
      } catch (e) {
        // Rethrown so the section keeps itself dirty — it only resets its
        // baseline when onSave resolves.
        toast.error(getErrorMessage(e));
        throw e;
      } finally {
        setNameSaving(false);
      }
    },
    [saveProfile, navigate, creatorData?.slug],
  );

  const saveAbout = useCallback(
    async (bio: string, location: UpdateCreatorLocationDto | null) => {
      setAboutSaving(true);
      try {
        await saveProfile({ bio, location });
      } catch (e) {
        toast.error(getErrorMessage(e));
        throw e;
      } finally {
        setAboutSaving(false);
      }
    },
    [saveProfile],
  );

  const saveLinks = useCallback(
    async (socials: UpdateCreatorSocialDto[]) => {
      setLinksSaving(true);
      try {
        await saveProfile({ socials });
      } catch (e) {
        toast.error(getErrorMessage(e));
        throw e;
      } finally {
        setLinksSaving(false);
      }
    },
    [saveProfile],
  );

  if (isInitialLoading) {
    return <CreatorPageSkeleton />;
  }

  return (
    <div
      className="w-full min-h-full"
      style={
        isEditing && canEdit
          ? undefined
          : {
              background: `radial-gradient(ellipse at center top, ${hexToRgba(primary, 0.12)} 0%, ${hexToRgba(primary, 0.06)} 50%, #fafbfd 70%)`,
            }
      }
    >
      <ContainerWrapper>
        <Flex
          direction={isDesktopLayout ? "row" : "column"}
          className={cn(
            "w-full self-center justify-self-center",
            isDesktopLayout ? "items-start pt-[25px] pb-[80px]" : "",
          )}
        >
          {/* Own artists get the private account nav even on the public
              route; visitors keep the marketplace filter sidebar */}
          {isDesktopLayout && !hideFilterSection && !canEdit && (
            <FilterSection />
          )}
          {isDesktopLayout && (showAudiolabNav || canEdit) && (
            <LeftNav type="account" />
          )}
          <Flex
            direction="column"
            className={cn(
              "flex-1 min-w-0 gap-6 px-4 pt-4 relative",
              isDesktopLayout && "pl-0 lg:pl-[50px]",
            )}
          >
            {/* Own profile: artist selector (switch which artist's profile
                you're viewing/editing; Pro gets the dropdown, other plans a
                link to the audiolab profile editor) */}
            {canEdit && !isEditing && (
              <div className="w-full max-w-[300px]">
                <ArtistSelector variant="modal" />
              </div>
            )}
            {/* Content wrapper */}
            <div className="relative w-full">
              {/* Main Content: Avatar + (Header + About + Links), then full-width table */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-10 w-full mt-4">
                {/* Left Column - 1/3: Avatar first on mobile (hidden in edit mode) */}
                <div
                  className={cn(
                    "w-full flex flex-col order-1",
                    isEditing && canEdit && "hidden",
                  )}
                >
                  <Flex direction="column" className="gap-6">
                    {/* Avatar - Full width, perfect circle */}
                    <Flex direction="column" className="gap-4">
                      {/* Wrapper matches avatar size so Update button stays centered on image */}
                      <div className="relative w-full max-w-[350px]">
                        {/* Outermost avatar wrapper - enforces 1:1 square, prevents oval */}
                        <div
                          className="w-full aspect-square rounded-full overflow-hidden shrink-0 border-[8px] border-solid"
                          style={{
                            borderColor: hexToRgba(primary, 0.35),
                          }}
                        >
                          {creatorData?.profilePictureUrl ? (
                            <img
                              src={
                                buildAvatarSrc(
                                  creatorData.profilePictureUrl,
                                  avatarCacheBuster,
                                ) || undefined
                              }
                              alt={creatorData?.stageName || "Artist"}
                              className="w-full h-full object-cover block"
                              onError={() => {
                                if (process.env.NODE_ENV === "production") {
                                  console.warn("[Avatar] failed", {
                                    creatorId: creatorData?.creatorId,
                                    src: buildAvatarSrc(
                                      creatorData.profilePictureUrl,
                                      avatarCacheBuster,
                                    ),
                                  });
                                }
                              }}
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center bg-gray-200"
                              style={{
                                color: primary,
                              }}
                            >
                              <Text size="9" weight="bold">
                                {creatorData?.stageName?.[0]?.toUpperCase() ||
                                  "A"}
                              </Text>
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <Button
                            onClick={handleAvatarUpload}
                            radius="full"
                            size="2"
                            color="gray"
                            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-30"
                          >
                            Update
                          </Button>
                        )}
                      </div>
                    </Flex>
                  </Flex>
                </div>

                {/* Right Column - 2/3: Name + Location + About + Links */}
                <div className="w-full lg:col-span-2 flex flex-col order-2">
                  <Flex direction="column" className="gap-4 lg:gap-6">
                    {/* Header: name/share/edit buttons */}
                    <div className="pl-0 lg:pl-4 pr-2">
                      <CreatorHeader
                        stageName={creatorData?.stageName || "Unknown Artist"}
                        creatorId={creatorData?.creatorId}
                        canEdit={canEdit}
                        onShare={handleShare}
                        onEdit={() => setIsEditing(true)}
                        onDoneEditing={handleDoneEditing}
                        isEditing={isEditing && canEdit}
                        location={creatorData?.location}
                        primaryInk={primaryInk}
                        primary={primary}
                        onPrimary={onPrimary}
                        wrapperClassName="flex flex-col"
                        hideFollow={forcePublicView}
                      />
                    </div>

                    {/* Content: read-only or edit sections */}
                    <div className="flex flex-col gap-10 lg:gap-6 pl-0 lg:pl-4 lg:pr-2">
                      {isEditing && canEdit ? (
                        <>
                          <InlineNameEditSection
                            initialName={creatorData?.stageName || ""}
                            initialSlug={creatorData?.slug}
                            avatarUrl={
                              buildAvatarSrc(
                                creatorData?.profilePictureUrl,
                                avatarCacheBuster,
                              ) || undefined
                            }
                            onAvatarClick={handleAvatarUpload}
                            isSaving={nameSaving}
                            onSave={saveName}
                            onDirtyChange={(d) =>
                              setSectionsDirty((prev) => ({
                                ...prev,
                                name: d,
                              }))
                            }
                          />
                          <InlineAboutEditSection
                            initialLocationText={
                              formatLocationDisplay(creatorData?.location) ||
                              creatorData?.location?.city ||
                              ""
                            }
                            initialLocationData={creatorData?.location}
                            initialBio={creatorData?.bio || ""}
                            isSaving={aboutSaving}
                            onSave={saveAbout}
                            onDirtyChange={(d) =>
                              setSectionsDirty((prev) => ({
                                ...prev,
                                about: d,
                              }))
                            }
                          />
                          <InlineLinksEditSection
                            initialLinks={(creatorData?.socials ?? [])
                              .filter((s) => !!s.url)
                              .map((s) => ({
                                url: s.url!,
                                channelCode: s.channel.code,
                              }))}
                            isSaving={linksSaving}
                            onSave={saveLinks}
                            onDirtyChange={(d) =>
                              setSectionsDirty((prev) => ({
                                ...prev,
                                links: d,
                              }))
                            }
                          />
                        </>
                      ) : (
                        <>
                          <AboutSection
                            text={creatorData?.bio || ""}
                            canEdit={false}
                            artistColor={primaryInk}
                          />
                          <LinksSection
                            links={creatorData?.socials || null}
                            canEdit={false}
                            artistColor={primaryInk}
                          />
                          {!!creatorData?.slug && (
                            <ClickToCopyButton
                              toCopy={getCreatorSlugUrl(creatorData.slug)}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </Flex>
                </div>

                {/* Full width: Filters and Tracks Table (order-3 so it appears below avatar + header) */}
                <div
                  className={cn(
                    "w-full lg:col-span-3 flex flex-col order-3",
                    isEditing && canEdit && "hidden",
                  )}
                >
                  {/* Filters and Search */}
                  <FilterBar className="mb-10 pl-4 pr-2" artistColor={primary}>
                    <Flex
                      direction={{ initial: "column", sm: "row" }}
                      className="items-start sm:items-center gap-4 w-full justify-between pt-6 pb-4"
                    >
                      {/* Filter Buttons - Only show when content check is complete */}
                      {!isContentCheckLoading && (
                        <TrackFilters
                          activeFilter={activeFilter}
                          setActiveFilter={setActiveFilter}
                          primary={primary}
                          onPrimary={onPrimary}
                          primaryInk={primaryInk}
                          outline={outline}
                          availableFilters={availableFilters}
                          filterCounts={filterCounts}
                        />
                      )}

                      {/* Search Input */}
                      {/* Mobile: Always expanded and centered */}
                      <Flex className="lg:hidden relative w-auto justify-center">
                        <TrackSearch
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search artist tracks"
                          className="max-w-[250px]"
                        />
                      </Flex>

                      {/* Desktop: Collapsible search */}
                      <Flex className="hidden lg:flex relative w-auto self-end">
                        {isSearchExpanded ? (
                          <TrackSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search artist tracks"
                            autoFocus
                            onBlur={() => {
                              // Close if search query is empty
                              if (!searchQuery.trim()) {
                                setIsSearchExpanded(false);
                              }
                            }}
                            onEscape={() => {
                              setIsSearchExpanded(false);
                              setSearchQuery("");
                            }}
                          />
                        ) : (
                          <button
                            onClick={() => setIsSearchExpanded(true)}
                            className="flex h-[34px] w-[34px] items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                            aria-label="Search tracks"
                          >
                            <HiOutlineMagnifyingGlass className="w-5 h-5 text-[#525966]" />
                          </button>
                        )}
                      </Flex>
                    </Flex>
                  </FilterBar>

                  {/* Tracks Table */}
                  <Box
                    className="w-full pb-32 md:pb-8"
                    data-testid="creator-profile-tracks"
                  >
                    {activeFilter === "ALBUM"
                      ? (() => {
                          // Check for error first
                          if (albumsQuery.isError) {
                            return (
                              <Flex
                                direction="column"
                                className="items-center justify-center py-12 gap-2"
                              >
                                <Text className="text-[#525966]" size="3">
                                  Failed to load albums
                                </Text>
                                <Text
                                  className="text-[#525966]"
                                  size="2"
                                  color="gray"
                                >
                                  Please try refreshing the page
                                </Text>
                              </Flex>
                            );
                          }

                          // Show loading state
                          if (isLoading) {
                            return (
                              <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div key={i} className="animate-pulse">
                                    <div className="aspect-square w-full rounded-lg bg-gray-200" />
                                    <div className="mt-[5px] h-4 w-3/4 rounded bg-gray-200" />
                                    <div className="mt-[6px] h-3 w-1/2 rounded bg-gray-200" />
                                  </div>
                                ))}
                              </Box>
                            );
                          }

                          const albums: ListAlbumDto[] =
                            albumsQuery.data?.pages.flatMap(
                              (page) => page.content ?? [],
                            ) ?? [];

                          if (!albums.length && !albumsQuery.isLoading) {
                            return (
                              <Flex className="items-center justify-center py-12">
                                <Text className="text-[#525966]">
                                  No albums available
                                </Text>
                              </Flex>
                            );
                          }

                          return (
                            <>
                              <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                                {albums.map((album, index) => (
                                  <ArtistAlbumCard
                                    key={
                                      album.albumId ??
                                      `${album.title}-${album.createdAt}-${index}`
                                    }
                                    album={album}
                                  />
                                ))}
                              </Box>
                              {hasMorePages && (
                                <ShowMoreButton
                                  onClick={handleLoadMore}
                                  disabled={isFetchingMore}
                                  isLoading={isFetchingMore}
                                  primary={primary}
                                  onPrimary={onPrimary}
                                  primaryInk={primaryInk}
                                  outline={outline}
                                />
                              )}
                            </>
                          );
                        })()
                      : (() => {
                          // Check for errors in tracks query
                          if (tracksQuery.isError) {
                            return (
                              <Flex
                                direction="column"
                                className="items-center justify-center py-12 gap-2"
                              >
                                <Text className="text-[#525966]" size="3">
                                  Failed to load tracks
                                </Text>
                                <Text
                                  className="text-[#525966]"
                                  size="2"
                                  color="gray"
                                >
                                  Please try refreshing the page
                                </Text>
                              </Flex>
                            );
                          }

                          // Show loading state
                          if (isLoading) {
                            return (
                              <Box role="table" className="w-full">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div
                                    key={i}
                                    className="flex items-center justify-between py-3 animate-pulse pl-4 pr-2"
                                  >
                                    <div className="h-4 w-48 bg-gray-200 rounded" />
                                  </div>
                                ))}
                              </Box>
                            );
                          }

                          return displayedTracks.length > 0 ? (
                            <>
                              <Box role="table">
                                {displayedTracks.map((track, index) => (
                                  <TrackRowWrapper
                                    key={track.trackId}
                                    track={track}
                                    artistColor={primary}
                                    hasBorder={index > 0}
                                  />
                                ))}
                              </Box>
                              {hasMorePages && (
                                <ShowMoreButton
                                  onClick={handleLoadMore}
                                  disabled={isFetchingMore}
                                  isLoading={isFetchingMore}
                                  primary={primary}
                                  onPrimary={onPrimary}
                                  primaryInk={primaryInk}
                                  outline={outline}
                                />
                              )}
                            </>
                          ) : (
                            <Flex className="items-center justify-center py-12">
                              <Text className="text-[#525966]">
                                {searchQuery
                                  ? "No tracks found matching your search"
                                  : "No tracks available"}
                              </Text>
                            </Flex>
                          );
                        })()}
                  </Box>
                </div>
              </div>
            </div>
          </Flex>
        </Flex>

        {shareModal}

        {showAvatarModal && creatorData?.creatorId && (
          <AddImageModal
            creatorId={creatorData.creatorId}
            target={{ type: "profile", creatorId: creatorData.creatorId }}
            title="Update profile photo"
            circular
            onClose={() => setShowAvatarModal(false)}
            onUpload={async (file) => {
              await uploadAvatarFile(file, refetchCreatorData);
            }}
            onSelectLibraryImage={async (source) => {
              await setProfileFromLibrary.mutateAsync({
                creatorId: creatorData.creatorId,
                data: { source },
              });
              await invalidateCreatorQueries(queryClient);
              await refetchCreatorData();
            }}
            onSaved={onAvatarSaved}
            isSaving={isUploading || setProfileFromLibrary.isPending}
          />
        )}
      </ContainerWrapper>
    </div>
  );
}
